/**
 * @file Pont evenements du core -> effets sonores synthetises (table data-driven evenement -> SfxId, variation de hauteur, limitation).
 */
import type { SfxId } from '@config/types';
import type { EventBus, GameEvents } from '@core/index';
import type { AudioEngine } from '@platform/audio/AudioEngine';
import type { WeaponBehavior } from '@shared/enums';

/** Comportements dont le tir est "lourd" (son grave). */
const HEAVY_BEHAVIORS: ReadonlySet<WeaponBehavior> = new Set<WeaponBehavior>(['explosive', 'missile', 'blastSniper']);
/** Espacement minimal (ms) de certains sons tres frequents, en plus de l'anti-spam du moteur. */
const HIT_GAP_MS = 50;
const COIN_GAP_MS = 80;
const AUTO_UPGRADE_GAP_MS = 900;

const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : 0);

/**
 * Abonnements : ProjectileFired / BeamFired -> 'shoot' (explosive/missile/blastSniper -> 'shootHeavy'), pitch 0.95..1.05 ;
 * EntityHit -> crit ? 'crit' : 'hit' (au plus 1 'hit' / 50 ms) ; Explosion -> 'explosion' ; TargetKilled -> tree ? 'treeFall' :
 * 'monsterDie' ; WalletChanged (money, delta > 0, reason 'kill') -> 'coin' (1 / 80 ms) ; UpgradeBought -> 'upgrade' ;
 * WeaponsMerged -> 'fusion' ; WeaponDrawn -> 'gacha' ; LevelCleared -> 'win' ; LevelFailed -> 'fail' ; RebirthDone -> 'rebirth' ;
 * StarUpgraded -> success ? 'starSuccess' : 'starFail' ; SkinBought -> 'purchase' ; BarrierDamaged -> 'barrierHit' ;
 * StageChanged -> 'levelUp' ; TargetSpawned (boss) -> 'spawn'. Retourne la fonction de desabonnement.
 * (BeamFired est ajoute : lasers et blastSniper n'emettent pas ProjectileFired.)
 */
export const bindAudio = (bus: EventBus<GameEvents>, audio: AudioEngine): (() => void) => {
  const play = (id: SfxId, pitch = 1): void => audio.play(id, pitch);
  const jitter = (): number => 0.95 + Math.random() * 0.1;
  let lastHit = -Infinity;
  let lastCoin = -Infinity;
  let lastAutoUpgrade = -Infinity;
  const gated = (id: SfxId, gapMs: number, last: number): number => {
    const t = nowMs();
    if (t - last < gapMs) return last;
    play(id);
    return t;
  };

  const offs: Array<() => void> = [
    bus.on('ProjectileFired', (e) => play(HEAVY_BEHAVIORS.has(e.behavior) ? 'shootHeavy' : 'shoot', jitter())),
    bus.on('BeamFired', (e) => play(HEAVY_BEHAVIORS.has(e.behavior) ? 'shootHeavy' : 'shoot', jitter())),
    bus.on('EntityHit', (e) => {
      if (e.crit) play('crit');
      else lastHit = gated('hit', HIT_GAP_MS, lastHit);
    }),
    bus.on('Explosion', () => play('explosion')),
    bus.on('TargetKilled', (e) => play(e.family === 'tree' ? 'treeFall' : 'monsterDie')),
    bus.on('WalletChanged', (e) => {
      if (e.id === 'money' && e.reason === 'kill' && e.delta.gt(0)) lastCoin = gated('coin', COIN_GAP_MS, lastCoin);
    }),
    // achats de l'amelioration auto (4 passages / s) : un son au plus par AUTO_UPGRADE_GAP_MS
    bus.on('UpgradeBought', (e) => {
      if (e.auto) lastAutoUpgrade = gated('upgrade', AUTO_UPGRADE_GAP_MS, lastAutoUpgrade);
      else play('upgrade');
    }),
    bus.on('WeaponsMerged', () => play('fusion')),
    bus.on('WeaponDrawn', () => play('gacha')),
    bus.on('LevelCleared', () => play('win')),
    bus.on('LevelFailed', () => play('fail')),
    bus.on('RebirthDone', () => play('rebirth')),
    bus.on('StarUpgraded', (e) => play(e.success ? 'starSuccess' : 'starFail')),
    bus.on('SkinBought', () => play('purchase')),
    bus.on('PetBought', () => play('purchase')),
    bus.on('BarrierDamaged', () => play('barrierHit')),
    bus.on('StageChanged', () => play('levelUp')),
    bus.on('TargetSpawned', (e) => {
      if (e.family === 'boss') play('spawn');
    }),
  ];
  return () => {
    for (const off of offs) off();
  };
};
